import { Controller } from "stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = [
    "selectAll",
    "durationHours",
    "durationMinutes",
    "durationTotal",
    "unassignedContainer",
    "unassignedRequiredLabel",
    "unassignedOptionalLabel",
    "unassignedText",
    "unassignedLabelDefaultPosition",
    "makeUpReassignmentContainer",
    "makeUpSessionSelectionContainer"
  ];

  static values = {
    sessionDurationMinutes: Number,
    // initialUnassignedMinutes and initialAttendanceDuration are baseline values from quick look load time.
    // We compare the baseline duration to the current edited duration, then subtract selected make-up session minutes.
    // This keeps “Requires X more” updated in the UI without another backend request.
    initialUnassignedMinutes: Number,
    initialAttendanceDuration: Number,
    enrolledSessionIds: Array,
    saveAttendance: { type: Boolean, default: false }
  };

  connect() {
    this.getInitialAttendanceValues();
    this.updateUnassignedDisplay();
    this.syncSelectAll();
  }

  getInitialAttendanceValues() {
    if (document._initialAttendanceValues) return;

    const snapshot = {};
    document.querySelectorAll("input[id^='attendance_duration_']").forEach(el => {
      const id = el.id.replace("attendance_duration_", "");
      snapshot[id] = {
        duration: el.value,
        notes: (document.getElementById(`attendance_notes_input_${id}`) || {}).value || "",
        move: this.moveValuesForAttendance(id).join(",")
      };
    });
    document._initialAttendanceValues = snapshot;
  }

  formHasChanges() {
    if (!document._initialAttendanceValues) return false;

    const snapshot = document._initialAttendanceValues;

    for (const id of Object.keys(snapshot)) {
      const duration = (document.getElementById(`attendance_duration_${id}`) || {}).value || "";
      const notes = (document.getElementById(`attendance_notes_input_${id}`) || {}).value || "";
      const move = this.moveValuesForAttendance(id).join(",");

      if (duration !== snapshot[id].duration ||
          notes !== snapshot[id].notes ||
          move !== snapshot[id].move) {
        return true;
      }
    }
    return false;
  }

  async showQuickLook(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    const durationMinutes = durationInput ? durationInput.value : null;
    const notesInput = document.getElementById(`attendance_notes_input_${attendanceId}`);
    const notesValue = notesInput ? notesInput.value : null;
    const moveValues = this.moveValuesForAttendance(attendanceId);

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const params = new URLSearchParams();
    if (durationMinutes !== null) {
      params.append("duration_in_minutes", durationMinutes);
    }
    if (notesValue !== null) {
      params.append("notes", notesValue);
    }
    moveValues.forEach((moveSessionId) => params.append("move_session_ids[]", moveSessionId));

    const saveAttendance = this.data.get("saveAttendance");
    if (saveAttendance === "true") {
      params.append("save_attendance", "true");
    }

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`/schools/classroom/sessions/attendance/${attendanceId}/quick-look${query}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const quickViewElement =
        document.querySelector("#school-classroom-session-attendance-quick-look") ||
        document.querySelector("#school_classrooms_quick_look");

      if (!quickViewElement) {
        console.error("Quick view element not found in the DOM.");
        return;
      }

      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      if (Turbo && typeof Turbo.renderStreamMessage === "function") {
        Turbo.renderStreamMessage(responseBody);
      } else {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseBody, "text/html");
        const template = xmlDoc.querySelector("template");

        if (template) {
          quickViewElement.innerHTML = "";
          quickViewElement.appendChild(template.content.cloneNode(true));
        }
      }
    }
  }

  moveValuesForAttendance(attendanceId) {
    const selector = `input[name='attendee_moves[${attendanceId}][]']`;
    return Array.from(document.querySelectorAll(selector))
      .map((input) => (input.value || "").trim())
      .filter((value) => value.length > 0);
  }

  closeQuickLook(event) {
    if (event) event.preventDefault();

    const quickViewElement = document.querySelector("#school-classroom-session-attendance-quick-look");

    if (!quickViewElement) {
      console.error("Quick view element not found in the DOM.");
      return;
    }

    quickViewElement.classList.add("u-hidden");
  }

  updateDuration() {
    if (!this.hasDurationHoursTarget || !this.hasDurationMinutesTarget || !this.hasDurationTotalTarget) {
      return;
    }

    const hours = parseInt(this.durationHoursTarget.value || "0", 10);
    const minutes = parseInt(this.durationMinutesTarget.value || "0", 10);
    const totalMinutes = (hours * 60) + minutes;

    this.durationTotalTarget.value = totalMinutes;

    this.updateUnassignedDisplay();
  }

  async validate(event) {
    event.preventDefault();

    const errors = this.validateForm();
    if (errors.length > 0) {
      this.showValidationBlock(errors.join("<br>"));
      return;
    }

    const form = event.target.closest("form");
    const formData = new FormData(form);
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    if (this.saveAttendanceValue) {
      formData.append("save_attendance", "true");
    }

    const response = await fetch(form.action, {
      method: form.method,
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    });

    if (this.saveAttendanceValue) {
      if (response.ok) {
        const responseBody = await response.text();
        if (Turbo && typeof Turbo.renderStreamMessage === "function") {
          Turbo.renderStreamMessage(responseBody);
        }
        this.showToast("Attendance updated");
      } else {
        const data = await response.json();
        this.showValidationBlock((data.errors || ["An error occurred"]).join("<br>"));
      }
      return;
    }

    if (response.ok) {
      const responseBody = await response.text();
      if (Turbo && typeof Turbo.renderStreamMessage === "function") {
        Turbo.renderStreamMessage(responseBody);
      }
      setTimeout(() => this.toggleUnsavedChangesBanner(), 100);
    }
  }

  validateForm() {
    let validationErrors = [];

    if (!this.hasDurationTotalTarget) {
      validationErrors.push("Duration is required.");
      return validationErrors;
    }

    const totalMinutes = parseInt(this.durationTotalTarget.value || "", 10);
    if (Number.isNaN(totalMinutes) || totalMinutes < 0) {
      validationErrors.push("Duration must be a valid number of minutes.");
      return validationErrors;
    }

    const maxMinutes = parseInt(this.durationTotalTarget.dataset.maxMinutes || "", 10);
    if (!Number.isNaN(maxMinutes) && maxMinutes > 0 && totalMinutes > maxMinutes) {
      validationErrors.push("Duration cannot exceed the session length.");
    }

    const alreadyEnrolledErrors = this.sessionAlreadyEnrolledErrors();
    if (alreadyEnrolledErrors.length > 0) {
      validationErrors = validationErrors.concat(alreadyEnrolledErrors);
    }

    const duplicateErrors = this.duplicateSessionSelectionErrors();
    if (duplicateErrors.length > 0) {
      validationErrors = validationErrors.concat(duplicateErrors);
    }

    return validationErrors;
  }

  showValidationBlock(message) {
    const validationBlock = document.getElementById("validation-block");
    const validationMessage = document.getElementById("validation-message");

    validationMessage.innerHTML = message;
    validationBlock.hidden = false;
    validationMessage.hidden = false;
  }

  hideValidationBlock() {
    const validationBlock = document.getElementById("validation-block");
    if (validationBlock) validationBlock.hidden = true;
  }

  validateSessionSelection() {
    const errors = [
      ...this.sessionAlreadyEnrolledErrors(),
      ...this.duplicateSessionSelectionErrors()
    ];
    const updateButton = document.getElementById("quick-look-update-button");
    this.updateUnassignedDisplay();

    if (errors.length > 0) {
      this.showValidationBlock(errors[0]);
      if (this.hasUnassignedContainerTarget) {
        this.unassignedContainerTarget.classList.add("u-hidden");
      }
      if (updateButton) updateButton.disabled = true;
    } else {
      this.hideValidationBlock();
      if (updateButton) updateButton.disabled = false;
    }
  }

  sessionAlreadyEnrolledErrors() {
    const errors = [];
    this.makeUpSessionSelectElements().forEach((sessionSelect) => {
      const selectedOption = sessionSelect.selectedOptions[0];
      if (!selectedOption || !selectedOption.value) return;

      const selectedId = parseInt(selectedOption.value, 10);
      const row = sessionSelect.closest("[data-row-key]");
      const hasExistingReassignment = !!(row && row.dataset.reassignmentId);
      const initialSessionId = parseInt((row && row.dataset.initialSessionId) || "", 10);

      // Existing reassignment rows can keep their own currently selected session.
      if (hasExistingReassignment && selectedId === initialSessionId) {
        return;
      }

      if (this.enrolledSessionIdsValue.includes(selectedId)) {
        errors.push("Student is already enrolled in the selected session.");
      }
    });
    return [...new Set(errors)];
  }

  duplicateSessionSelectionErrors() {
    const selectedIds = this.makeUpSessionSelectElements()
      .map((sessionSelect) => parseInt(sessionSelect.value || "", 10))
      .filter((value) => Number.isInteger(value) && value > 0);

    if (selectedIds.length === 0) {
      return [];
    }

    if (new Set(selectedIds).size !== selectedIds.length) {
      return ["The same make-up session cannot be selected more than once."];
    }

    return [];
  }

  makeUpSessionSelectElements() {
    return Array.from(this.element.querySelectorAll("[name^='attendee_moves']"));
  }

  sessionDurationFromSelectOption(option) {
    if (!option) return 0;
    const optionMinutes = parseInt(option.dataset.durationMinutes || "0", 10);
    return Number.isNaN(optionMinutes) ? 0 : optionMinutes;
  }

  selectedMakeUpSessionDeltaMinutes() {
    return this.makeUpRowElements().reduce((total, rowElement) => {
      const sessionSelect = rowElement.querySelector("select[name^='attendee_moves']");
      if (!sessionSelect) return total;

      const selectedOption = sessionSelect.selectedOptions[0];
      const selectedMinutes =
        selectedOption && selectedOption.value
          ? this.sessionDurationFromSelectOption(selectedOption)
          : 0;

      const initialSessionId = (rowElement.dataset.initialSessionId || "").trim();
      let initialMinutes = 0;

      if (initialSessionId.length > 0) {
        const initialOption = Array.from(sessionSelect.options).find(
          (option) => (option.value || "").trim() === initialSessionId
        );
        initialMinutes = this.sessionDurationFromSelectOption(initialOption);
      }

      return total + (selectedMinutes - initialMinutes);
    }, 0);
  }

  updateUnassignedDisplay() {
    if (!this.hasUnassignedContainerTarget || !this.hasUnassignedTextTarget) {
      return;
    }

    const currentDurationMinutes = this.hasDurationTotalTarget
      ? parseInt(this.durationTotalTarget.value || "0", 10)
      : this.initialAttendanceDurationValue || 0;

    const initialUnassignedMinutes = Number.isFinite(this.initialUnassignedMinutesValue) ? this.initialUnassignedMinutesValue : 0;
    const initialDurationMinutes = Number.isFinite(this.initialAttendanceDurationValue) ? this.initialAttendanceDurationValue : 0;

    // If duration changes in the form, adjust unassigned by that difference.
    const adjustedUnassignedMinutes = Math.max(initialUnassignedMinutes + (initialDurationMinutes - currentDurationMinutes), 0);
    const selectedMakeUpSessionDeltaMinutes = this.selectedMakeUpSessionDeltaMinutes();
    const remainingMinutes = adjustedUnassignedMinutes - selectedMakeUpSessionDeltaMinutes;
    this.updateMakeUpSessionSelectionRows(remainingMinutes);
    const hasUnselectedAdditionalRow = this.firstUnselectedAdditionalRow(this.makeUpRowElements()).present;
    const shouldShowOptionalLabel = remainingMinutes <= 0 && hasUnselectedAdditionalRow;

    if (remainingMinutes > 0) {
      this.unassignedContainerTarget.classList.remove("u-hidden");
      this.unassignedTextTarget.textContent = this.formatDuration(Math.max(remainingMinutes, 0));
      this.unassignedContainerTarget.classList.add("u-textRed");
      if (this.hasUnassignedRequiredLabelTarget) {
        this.unassignedRequiredLabelTarget.classList.remove("u-hidden");
      }
      if (this.hasUnassignedOptionalLabelTarget) {
        this.unassignedOptionalLabelTarget.classList.add("u-hidden");
      }
    } else if (shouldShowOptionalLabel) {
      this.unassignedContainerTarget.classList.remove("u-hidden");
      this.unassignedContainerTarget.classList.remove("u-textRed");
      if (this.hasUnassignedRequiredLabelTarget) {
        this.unassignedRequiredLabelTarget.classList.add("u-hidden");
      }
      if (this.hasUnassignedOptionalLabelTarget) {
        this.unassignedOptionalLabelTarget.classList.remove("u-hidden");
      }
    } else {
      this.unassignedContainerTarget.classList.add("u-hidden");
      if (this.hasUnassignedRequiredLabelTarget) {
        this.unassignedRequiredLabelTarget.classList.remove("u-hidden");
      }
      if (this.hasUnassignedOptionalLabelTarget) {
        this.unassignedOptionalLabelTarget.classList.add("u-hidden");
      }
    }
  }

  // Keeps make-up session rows in sync with the remaining minutes to assign by:
  // 1) Removing extra blank rows at the end (so we keep at most one empty row)
  // 2) If every visible row has a selected session, auto-adding one more empty row
  updateMakeUpSessionSelectionRows(remainingMinutes) {
    if (!this.hasMakeUpSessionSelectionContainerTarget) {
      return;
    }

    // Keep only one empty row at the end so the UI does not grow with blank duplicates
    this.removeTrailingBlankRows();

    const makeUpSessionSelects = this.makeUpSessionSelectElements();
    if (makeUpSessionSelects.length === 0) {
      return;
    }

    const hasBlankRow = makeUpSessionSelects.some((sessionSelect) => !(sessionSelect.value || "").trim());
    const allRowsSelected = !hasBlankRow;
    const hasSelectionValidationErrors = this.hasMakeUpSelectionValidationErrors();

    // If every visible row has a selection auto-add one more empty dropdown row.
    // Do not auto-add while selections are invalid (e.g. duplicate session selected).
    if (allRowsSelected && !hasSelectionValidationErrors) {
      this.appendEmptyMakeUpRow();
    }

    this.updateMakeUpRowDisplay(remainingMinutes);
  }

  hasMakeUpSelectionValidationErrors() {
    return (
      this.sessionAlreadyEnrolledErrors().length > 0 ||
      this.duplicateSessionSelectionErrors().length > 0
    );
  }

  updateMakeUpRowDisplay(remainingMinutes = 0) {
    const rowElements = this.makeUpRowElements();
    if (rowElements.length === 0) {
      return;
    }

    const hasAdditionalRows = rowElements.length > 1;
    if (this.hasUnassignedContainerTarget) {
      this.unassignedContainerTarget.dataset.forAdditionalRows = hasAdditionalRows ? "true" : "false";
      this.unassignedContainerTarget.classList.toggle("is-for-additional-rows", hasAdditionalRows);
    }
    this.updateUnassignedLabelPosition(hasAdditionalRows, rowElements);

    rowElements.forEach((rowElement, index) => {
      const isAdditionalRow = index > 0;
      const sessionSelect = rowElement.querySelector("select[name^='attendee_moves']");
      const hasSelection = !!((sessionSelect && sessionSelect.value) ? sessionSelect.value.trim() : "");
      const isOptionalBlankAdditionalRow = isAdditionalRow && !hasSelection && remainingMinutes <= 0;

      rowElement.classList.remove("Panel--alert", "Panel--bright");

      if (isAdditionalRow) {
        rowElement.classList.add("Panel", "Panel--borderless", "p2", "mt15")
        rowElement.classList.add(
          hasSelection || isOptionalBlankAdditionalRow ? "Panel--bright" : "Panel--alert"
        );
      }
    });
  }

  updateUnassignedLabelPosition(hasAdditionalRows, rowElements) {
    if (!this.hasUnassignedContainerTarget) {
      return;
    }

    if (hasAdditionalRows) {
      const additionalRows = rowElements.slice(1);
      const firstUnselectedAdditionalRow = this.firstUnselectedAdditionalRow(rowElements).row;

      const targetAdditionalRow = firstUnselectedAdditionalRow || additionalRows[0];
      if (targetAdditionalRow) {
        targetAdditionalRow.insertBefore(this.unassignedContainerTarget, targetAdditionalRow.firstChild);
        return;
      }
    }

    if (this.hasUnassignedLabelDefaultPositionTarget) {
      const defaultPositionParent = this.unassignedLabelDefaultPositionTarget.parentNode;
      if (defaultPositionParent) {
        defaultPositionParent.insertBefore(this.unassignedContainerTarget, this.unassignedLabelDefaultPositionTarget);
      }
    }
  }

  removeTrailingBlankRows() {
    const rowElements = this.makeUpRowElements();
    if (rowElements.length <= 1) {
      return;
    }

    for (let index = rowElements.length - 1; index >= 1; index -= 1) {
      const rowElement = rowElements[index];
      if (!rowElement) continue;

      // Never remove saved rows loaded from the backend.
      if (rowElement.dataset.reassignmentId) break;
      // Also keep pending move rows restored from the attendance row
      if (rowElement.dataset.initialSessionId) break;

      const sessionSelect = rowElement.querySelector("select[name^='attendee_moves']");
      const selectedSessionId = (sessionSelect && sessionSelect.value) ? sessionSelect.value.trim() : "";
      if (selectedSessionId) break;

      // Remove extra unsaved blank rows from the end.
      if (this.hasUnassignedContainerTarget && rowElement.contains(this.unassignedContainerTarget)) {
        if (this.hasUnassignedLabelDefaultPositionTarget && this.unassignedLabelDefaultPositionTarget.parentNode) {
          this.unassignedLabelDefaultPositionTarget.parentNode.insertBefore(
            this.unassignedContainerTarget,
            this.unassignedLabelDefaultPositionTarget
          );
        } else if (this.makeUpReassignmentContainerTarget) {
          this.makeUpReassignmentContainerTarget.appendChild(this.unassignedContainerTarget);
        }
      }
      rowElement.remove();
    }
  }

  appendEmptyMakeUpRow() {
    // Clone the first row so new rows keep the same template/program controls and behavior
    const sourceRow = this.makeUpRowElements()[0];
    if (!sourceRow) return;

    const clonedRow = sourceRow.cloneNode(true);
    const nextRowKey = this.nextNewRowKey();

    clonedRow.dataset.rowKey = nextRowKey;
    delete clonedRow.dataset.reassignmentId;
    delete clonedRow.dataset.initialSessionId;

    const reassignElement = clonedRow.querySelector("[data-controller='driving-school-classroom-session-reassign']");
    if (reassignElement) {
      // New rows should not preload a selected make-up session
      reassignElement.removeAttribute("data-driving-school-classroom-session-reassign-move-session-id-value");
    }

    clonedRow.querySelectorAll("select[name^='reassign_templates']").forEach((templateSelect) => {
      templateSelect.name = `reassign_templates[${nextRowKey}]`;
    });

    clonedRow.querySelectorAll("select[name^='attendee_moves']").forEach((sessionSelect) => {
      sessionSelect.name = `attendee_moves[${nextRowKey}]`;
      sessionSelect.value = "";
    });

    this.makeUpSessionSelectionContainerTarget.appendChild(clonedRow);
  }

  nextNewRowKey() {
    // Row keys are "new_1", "new_2", ...
    const keys = this.makeUpRowElements()
      .map((rowElement) => rowElement.dataset.rowKey || "")
      .map((rowKey) => {
        const match = rowKey.match(/^new_(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((value) => Number.isInteger(value));

    const maxKey = keys.length > 0 ? Math.max(...keys) : 1;
    return `new_${maxKey + 1}`;
  }

  makeUpRowElements() {
    if (!this.hasMakeUpSessionSelectionContainerTarget) {
      return [];
    }

    // Each reassignment row wrapper carries a data-row-key
    return Array.from(this.makeUpSessionSelectionContainerTarget.querySelectorAll("[data-row-key]"));
  }

  firstUnselectedAdditionalRow(rowElements) {
    const additionalRows = Array.isArray(rowElements) ? rowElements.slice(1) : [];
    const row = additionalRows.find((rowElement) => {
      const sessionSelect = rowElement.querySelector("select[name^='attendee_moves']");
      const selectedSessionId = (sessionSelect && sessionSelect.value) ? sessionSelect.value.trim() : "";
      return selectedSessionId.length === 0;
    });

    return {
      row,
      present: !!row
    };
  }

  toggleUnsavedChangesBanner() {
    const banner = document.getElementById("unsaved-changes-message");
    if (!banner) return;

    if (this.formHasChanges()) {
      banner.classList.remove("u-hidden");
    } else {
      banner.classList.add("u-hidden");
    }
  }

  toggleAttendance(event) {
    const checkbox = event.target;
    if (!checkbox) {
      return;
    }

    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    this.setDurationForCheckbox(checkbox);
    this.toggleUnsavedChangesBanner();
    this.syncSelectAll();
  }

  toggleAll(event) {
    const checked = event.target.checked;
    this.attendanceCheckboxes().forEach((cb) => {
      cb.checked = checked;
      this.setDurationForCheckbox(cb);
    });
    this.toggleUnsavedChangesBanner();
  }

  attendanceCheckboxes() {
    return Array.from(this.element.querySelectorAll("input.Input--checkbox-input[data-attendance-id]"));
  }

  syncSelectAll() {
    if (!this.hasSelectAllTarget) return;

    const checkboxes = this.attendanceCheckboxes();
    this.selectAllTarget.checked = checkboxes.length > 0 && checkboxes.every((checkbox) => checkbox.checked);
  }

  setDurationForCheckbox(checkbox) {
    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    if (!durationInput) {
      return;
    }

    const sessionMinutes = Number.isFinite(this.sessionDurationMinutesValue)
      ? this.sessionDurationMinutesValue
      : 0;

    durationInput.value = checkbox.checked ? sessionMinutes : 0;

    const displayElement = document.getElementById(`attendance_duration_display_${attendanceId}`);
    if (displayElement) {
      displayElement.textContent = this.formatDuration(durationInput.value);
    }

    const durationIcon = document.getElementById(`attendance_duration_icon_${attendanceId}`);
    if (durationIcon) {
      const isFullDuration = parseInt(durationInput.value, 10) === sessionMinutes;
      durationIcon.classList.toggle("u-hidden", isFullDuration);
    }
  }

  formatDuration(durationMinutes) {
    const total = parseInt(durationMinutes || "0", 10);
    if (Number.isNaN(total) || total === 0) {
      return "-";
    }

    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    if (hours === 0) {
      return `${minutes}m`;
    }
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }

  showToast(message) {
    window.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message }
    }));
  }
}
